import LegalLayout, {
  useLegalCompanyInfo, Section, InfoTable, AlertBox, Highlight
} from './LegalLayout';

const SECTIONS = [
  { id: 'responsable', label: '1. Responsable del tratamiento' },
  { id: 'datos', label: '2. Datos personales recabados' },
  { id: 'finalidad', label: '3. Finalidad del tratamiento' },
  { id: 'base-legal', label: '4. Base legal' },
  { id: 'transferencia', label: '5. Transferencia de datos' },
  { id: 'conservacion', label: '6. Conservación de datos' },
  { id: 'derechos', label: '7. Derechos ARCO' },
  { id: 'menores', label: '8. Menores de edad' },
  { id: 'seguridad', label: '9. Seguridad' },
  { id: 'cookies', label: '10. Cookies y tecnologías' },
  { id: 'cambios', label: '11. Cambios a la política' },
  { id: 'contacto', label: '12. Contacto DPA' },
];

export default function PrivacidadPage() {
  const co = useLegalCompanyInfo();

  return (
    <LegalLayout
      title="Política de Privacidad"
      subtitle="Tu privacidad es importante para nosotros. Esta política explica cómo recopilamos, usamos y protegemos tu información personal conforme a la LFPDPPP."
      sections={SECTIONS}
      lastUpdated={co.last_updated}
    >
      <AlertBox type="orange">
        La presente Política de Privacidad cumple con lo establecido en la <Highlight>Ley Federal de Protección de Datos Personales en Posesión de los Particulares (LFPDPPP)</Highlight> y su Reglamento, vigentes en los Estados Unidos Mexicanos.
      </AlertBox>

      <Section id="responsable" title="1. Responsable del Tratamiento de Datos">
        <p>El responsable del tratamiento de tus datos personales es:</p>
        <InfoTable rows={[
          ['Nombre o razón social', co.legal_name || co.name],
          ['RFC', co.rfc || '—'],
          ['Domicilio', co.address ? `${co.address}, ${co.city}, ${co.state} ${co.zip_code}, ${co.country}` : '—'],
          ['Teléfono', co.phone || '—'],
          ['Correo electrónico', co.email],
          ['Correo privacidad (DPA)', co.dpa_email],
          ['Sitio web', co.website],
        ]} />
        <p>Como responsable, <Highlight>{co.name}</Highlight> determina las finalidades y los medios del tratamiento de tus datos personales, actuando de conformidad con los principios de licitud, consentimiento, información, calidad, finalidad, lealtad, proporcionalidad y responsabilidad establecidos en la LFPDPPP.</p>
      </Section>

      <Section id="datos" title="2. Datos Personales que Recabamos">
        <p>Dependiendo de si utilizas OkReserva como restaurante o como comensal, recabamos distintas categorías de datos:</p>

        <h3 className="text-lg font-bold text-gray-900 mt-6 mb-3">A) Datos de Restaurantes (Usuarios Negocio)</h3>
        <InfoTable rows={[
          ['Identificación', 'Nombre completo del representante legal, RFC, CURP'],
          ['Contacto', 'Correo electrónico, teléfono, WhatsApp'],
          ['Negocio', 'Nombre del restaurante, dirección, horarios, capacidad'],
          ['Bancarios', 'CLABE, información bancaria para cobros (procesada por proveedor certificado)'],
          ['Técnicos', 'Dirección IP, logs de actividad, dispositivo y navegador'],
        ]} />

        <h3 className="text-lg font-bold text-gray-900 mt-6 mb-3">B) Datos de Comensales (Usuarios Finales)</h3>
        <InfoTable rows={[
          ['Identificación', 'Nombre, apellidos, apodo (nickname)'],
          ['Contacto', 'Correo electrónico, teléfono, WhatsApp'],
          ['Preferencias', 'Preferencias alimentarias, alergias y restricciones dietéticas'],
          ['Reservaciones', 'Historial de reservaciones, restaurantes visitados, fechas y horarios'],
          ['Lealtad', 'Puntos acumulados, nivel de membresía, beneficios canjeados'],
          ['Cumpleaños', 'Fecha de cumpleaños (opcional, para beneficios especiales)'],
          ['Técnicos', 'Dirección IP, geolocalización aproximada, dispositivo y navegador'],
          ['Pago', 'Datos de transacciones de depósitos y gift cards (sin números de tarjeta completos)'],
        ]} />

        <AlertBox type="info">
          <Highlight>Datos sensibles:</Highlight> Las preferencias alimentarias y alergias se consideran datos de categoría especial conforme al artículo 9 de la LFPDPPP. Su tratamiento requiere consentimiento expreso y está estrictamente limitado a la prestación del servicio de seguridad alimentaria.
        </AlertBox>
      </Section>

      <Section id="finalidad" title="3. Finalidad del Tratamiento">
        <p>Los datos personales que recabamos son utilizados para las siguientes finalidades:</p>

        <h3 className="text-lg font-bold text-gray-900 mt-4 mb-3">Finalidades primarias (necesarias para el servicio)</h3>
        <InfoTable rows={[
          ['Gestión de cuenta', 'Creación, autenticación y mantenimiento de cuentas de usuario.'],
          ['Prestación del servicio', 'Gestión de reservaciones, asignación de mesas y comunicación de confirmaciones.'],
          ['Seguridad alimentaria', 'Alertas al personal del restaurante sobre alérgenos del comensal.'],
          ['Facturación', 'Emisión de CFDI y cobro de servicios contratados.'],
          ['Soporte técnico', 'Atención a consultas, resolución de incidencias y mejora del servicio.'],
          ['Comunicaciones transaccionales', 'Confirmaciones, recordatorios y actualizaciones de reservaciones.'],
        ]} />

        <h3 className="text-lg font-bold text-gray-900 mt-4 mb-3">Finalidades secundarias (requieren consentimiento)</h3>
        <InfoTable rows={[
          ['Marketing personalizado', 'Envío de promociones, ofertas especiales y novedades de OkReserva.'],
          ['Análisis y mejora', 'Análisis estadístico del uso de la plataforma para mejorar la experiencia.'],
          ['Comunicaciones de restaurantes', 'Envío de notificaciones y ofertas de restaurantes donde has reservado.'],
          ['Programa de lealtad', 'Gestión de puntos, beneficios y comunicaciones del programa de lealtad.'],
          ['NPS y satisfacción', 'Encuestas post-visita para medir la calidad del servicio.'],
          ['Publicidad segmentada', 'Mostrarte anuncios relevantes basados en tus preferencias.'],
        ]} />
        <p>Puedes retirar tu consentimiento para las finalidades secundarias en cualquier momento desde la sección "Configuración de privacidad" en tu perfil, sin que ello afecte la prestación del servicio principal.</p>
      </Section>

      <Section id="base-legal" title="4. Base Legal del Tratamiento">
        <p>El tratamiento de tus datos personales se realiza bajo las siguientes bases legales conforme a la LFPDPPP:</p>
        <InfoTable rows={[
          ['Ejecución de contrato', 'Para prestarte los servicios contratados (Art. 8 LFPDPPP).'],
          ['Consentimiento expreso', 'Para finalidades secundarias y datos sensibles (Art. 9 LFPDPPP).'],
          ['Obligación legal', 'Para cumplir con obligaciones fiscales y regulatorias (Art. 8 LFPDPPP).'],
          ['Interés legítimo', 'Para prevención de fraudes y seguridad de la plataforma.'],
        ]} />
      </Section>

      <Section id="transferencia" title="5. Transferencia de Datos Personales">
        <p>OkReserva puede compartir tus datos personales con terceros en los siguientes supuestos:</p>

        <h3 className="text-lg font-bold text-gray-900 mt-4 mb-3">Transferencias que no requieren consentimiento (Art. 37 LFPDPPP)</h3>
        <InfoTable rows={[
          ['Autoridades competentes', 'Respuesta a requerimientos legales de autoridades mexicanas (INAI, PROFECO, SAT, etc.)'],
          ['Procesadores de pago', 'Stripe, Conekta u otros proveedores certificados, para procesamiento de transacciones.'],
          ['Proveedores de infraestructura', 'Servicios de nube (AWS, Google Cloud) para hospedaje de la plataforma.'],
        ]} />

        <h3 className="text-lg font-bold text-gray-900 mt-4 mb-3">Transferencias que requieren consentimiento</h3>
        <InfoTable rows={[
          ['Restaurantes asociados', 'Tus datos de contacto y preferencias se comparten con los restaurantes donde realizas reservaciones.'],
          ['Plataformas de integración', 'OpenTable, TheFork, Google Reserve (solo cuando utilizas integración con dichas plataformas).'],
          ['Socios comerciales', 'Para ofertas y promociones especiales (solo con tu consentimiento previo).'],
        ]} />

        <AlertBox type="warning">
          OkReserva no vende ni alquila tus datos personales a terceros con fines comerciales. Toda transferencia se realiza conforme a contratos que garantizan el mismo nivel de protección que esta Política.
        </AlertBox>

        <p><Highlight>Transferencias internacionales.</Highlight> Algunos de nuestros proveedores de servicios se encuentran fuera de México (AWS en EE.UU., Google Cloud). Estas transferencias se realizan bajo mecanismos adecuados que garantizan un nivel de protección equivalente al requerido por la LFPDPPP, incluyendo Cláusulas Contractuales Tipo y certificaciones de privacidad.</p>
      </Section>

      <Section id="conservacion" title="6. Período de Conservación de Datos">
        <p>Conservamos tus datos personales durante el tiempo necesario para cumplir con las finalidades descritas y conforme a las obligaciones legales aplicables:</p>
        <InfoTable rows={[
          ['Datos de cuenta activa', 'Mientras la cuenta permanezca activa.'],
          ['Datos post-cancelación', '3 años para datos de contacto; 5 años para datos fiscales (SAT).'],
          ['Historial de reservaciones', '5 años para efectos de auditoría y prevención de fraudes.'],
          ['Logs y registros técnicos', '12 meses.'],
          ['Datos de menores', 'Se eliminan inmediatamente cuando son detectados.'],
          ['Datos para obligaciones fiscales', '5 años conforme al Código Fiscal de la Federación.'],
        ]} />
        <p>Una vez vencido el período de conservación, los datos serán eliminados de forma segura o anonimizados para su uso estadístico.</p>
      </Section>

      <Section id="derechos" title="7. Tus Derechos ARCO">
        <p>Conforme a la LFPDPPP, tienes derecho a ejercer los siguientes derechos respecto de tus datos personales:</p>
        <InfoTable rows={[
          ['Acceso (A)', 'Conocer qué datos personales tenemos sobre ti, para qué los usamos y las condiciones de su tratamiento.'],
          ['Rectificación (R)', 'Solicitar la corrección de datos inexactos, incompletos o desactualizados.'],
          ['Cancelación (C)', 'Pedir la eliminación de tus datos cuando ya no sean necesarios para la finalidad que motivó su recopilación.'],
          ['Oposición (O)', 'Oponerte al tratamiento de tus datos para finalidades específicas, especialmente para marketing.'],
        ]} />

        <h3 className="text-lg font-bold text-gray-900 mt-4 mb-2">¿Cómo ejercer tus derechos ARCO?</h3>
        <p>Puedes ejercer tus derechos ARCO enviando una solicitud a:</p>
        <InfoTable rows={[
          ['Correo electrónico', co.dpa_email || co.email],
          ['Asunto', '"Solicitud de Derechos ARCO — [Tu nombre completo]"'],
          ['Tiempo de respuesta', '20 días hábiles a partir de la recepción de la solicitud (prorrogable 20 días más).'],
          ['Documentación requerida', 'Copia de identificación oficial vigente y descripción de la solicitud.'],
        ]} />

        <AlertBox type="success">
          También puedes ejercer tus derechos directamente desde tu cuenta de usuario en la sección "Configuración → Privacidad y datos". Algunas solicitudes (como actualización de datos de contacto) pueden resolverse de forma inmediata desde la plataforma.
        </AlertBox>

        <p><Highlight>Revocación del consentimiento.</Highlight> Tienes derecho a revocar el consentimiento otorgado para el tratamiento de tus datos en cualquier momento, sin efecto retroactivo. La revocación no afectará tratamientos basados en otras bases legales (como el cumplimiento de obligaciones contractuales).</p>
        <p><Highlight>Recurso ante el INAI.</Highlight> Si consideras que tu solicitud no fue atendida correctamente, puedes presentar una queja ante el Instituto Nacional de Transparencia, Acceso a la Información y Protección de Datos Personales (INAI): <a href="https://www.inai.org.mx" target="_blank" rel="noopener noreferrer" className="text-orange-600 underline">www.inai.org.mx</a>.</p>
      </Section>

      <Section id="menores" title="8. Menores de Edad">
        <p>OkReserva no está dirigida a menores de 18 años. No recopilamos intencionalmente datos personales de menores de edad. Si eres padre o tutor y crees que tu hijo ha proporcionado datos personales en nuestra plataforma, contáctanos de inmediato a <strong>{co.dpa_email || co.email}</strong> y procederemos a eliminar dicha información.</p>
        <p>En caso de confirmarse que hemos recopilado datos de un menor sin consentimiento parental, los eliminaremos de inmediato conforme al artículo 9 de la LFPDPPP.</p>
      </Section>

      <Section id="seguridad" title="9. Medidas de Seguridad">
        <p>OkReserva implementa medidas técnicas, administrativas y físicas para proteger tus datos personales:</p>
        <InfoTable rows={[
          ['Cifrado en tránsito', 'TLS 1.3 para toda comunicación entre tu dispositivo y nuestros servidores.'],
          ['Cifrado en reposo', 'AES-256 para datos almacenados en base de datos.'],
          ['Autenticación', 'PIN de 4 dígitos + sesiones con JWT de corta duración para el portal del cliente.'],
          ['Control de acceso', 'Principio de mínimo privilegio; solo personal autorizado accede a datos personales.'],
          ['Auditoría', 'Logs de acceso y modificación de datos con retención de 12 meses.'],
          ['Pruebas de seguridad', 'Pruebas de penetración periódicas y escaneo de vulnerabilidades.'],
          ['Plan de respuesta a incidentes', 'Notificación al INAI y a los usuarios afectados en un máximo de 72 horas ante brechas de seguridad.'],
        ]} />
        <AlertBox type="warning">
          A pesar de nuestras medidas de seguridad, ningún sistema es completamente invulnerable. En caso de una brecha de seguridad que afecte tus datos, te notificaremos de inmediato conforme al artículo 20 de la LFPDPPP.
        </AlertBox>
      </Section>

      <Section id="cookies" title="10. Cookies y Tecnologías de Rastreo">
        <p>Utilizamos cookies y tecnologías similares en nuestra plataforma. Para más información detallada sobre qué cookies usamos, consulta nuestra <a href="/cookies" className="text-orange-600 underline">Política de Cookies</a>.</p>
        <p>En resumen, utilizamos cookies para:</p>
        <ul className="list-disc list-inside ml-4 space-y-1">
          <li>Mantener tu sesión activa (cookies esenciales)</li>
          <li>Recordar tus preferencias (cookies de preferencia)</li>
          <li>Analizar el uso de la plataforma para mejorarla (cookies analíticas)</li>
          <li>Personalizar contenido y ofertas (cookies de marketing, solo con consentimiento)</li>
        </ul>
      </Section>

      <Section id="cambios" title="11. Cambios a esta Política de Privacidad">
        <p>OkReserva puede actualizar esta Política de Privacidad periódicamente para reflejar cambios en nuestras prácticas o en la legislación aplicable. Cuando realicemos cambios materiales:</p>
        <ul className="list-disc list-inside ml-4 space-y-1">
          <li>Publicaremos la nueva versión con su fecha de actualización en esta página</li>
          <li>Te notificaremos por correo electrónico o mediante aviso en la plataforma</li>
          <li>Solicitaremos tu consentimiento cuando los cambios afecten finalidades para las que ya diste tu consentimiento</li>
        </ul>
        <p>El uso continuado de la Plataforma después de la publicación de los cambios implica su aceptación.</p>
      </Section>

      <Section id="contacto" title="12. Contacto — Responsable de Protección de Datos">
        <p>Para cualquier consulta, solicitud o queja relacionada con el tratamiento de tus datos personales, puedes contactar a nuestro responsable de protección de datos:</p>
        <InfoTable rows={[
          ['Responsable de Datos (DPA)', co.legal_name || co.name],
          ['Correo electrónico', co.dpa_email || co.email],
          ['Teléfono', co.phone || '—'],
          ['Domicilio', co.address ? `${co.address}, ${co.city}, ${co.state}, ${co.country}` : '—'],
          ['Horario de atención', 'Lunes a Viernes, 9:00 AM – 6:00 PM (hora Ciudad de México)'],
          ['Tiempo de respuesta', '20 días hábiles (prorrogable)'],
        ]} />
        <AlertBox type="info">
          OkReserva está inscrita en el Registro de Responsables de Tratamiento de Datos Personales del INAI. Puedes verificar nuestra inscripción en <a href="https://www.inai.org.mx" target="_blank" rel="noopener noreferrer" className="text-orange-600 underline">www.inai.org.mx</a>.
        </AlertBox>
      </Section>
    </LegalLayout>
  );
}
